import React, { useState } from 'react';
import { MdSearch, MdGroupAdd, MdClose } from 'react-icons/md';
import moment from 'moment';

/**
 * ChatListPane component displays the list of active chats and user search results.
 * Now using Inline Styles instead of Tailwind CSS.
 */
const ChatListPane = ({
    searchQuery,
    setSearchQuery,
    filteredUsers,
    handleStartNewChat,
    chats,
    selectedChat,
    setSelectedChat,
    getChatPartnerDetails,
    isVisible,
    onStartGroupChat,
    chatType,
    setChatType,
    currentUserId,
    handleRemoveChat
}) => {

    // --- 1. Helper Functions ---

    const calculateUnreadCount = (chat) => {
        return chat.unreadCount || 0;
    };

    const filteredChats = chats.filter(chat => {
        if (chatType === 'groups') {
            return chat.isGroup;
        } else {
            return !chat.isGroup;
        }
    }).sort((a, b) => {
        const unreadA = calculateUnreadCount(a) > 0 ? 1 : 0;
        const unreadB = calculateUnreadCount(b) > 0 ? 1 : 0;
        if (unreadB !== unreadA) return unreadB - unreadA;
        const timeA = a.lastMessageTimestamp?.seconds || 0;
        const timeB = b.lastMessageTimestamp?.seconds || 0;
        return timeB - timeA;
    });

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
        if (moment(date).isSame(moment(), 'day')) {
            return moment(date).format('HH:mm');
        } else if (moment(date).isSame(moment().subtract(1, 'days'), 'day')) {
            return 'Yesterday';
        } else {
            return moment(date).format('DD/MM/YY');
        }
    };

    // --- 2. Inline Styles Object ---
    const styles = {
        paneContainer: {
            display: isVisible ? 'flex' : 'none', // Controlled by parent
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            backgroundColor: 'white',
            borderRight: '1px solid #e5e7eb',
            // On desktop, parent usually constrains width, but we ensure it fills available space
        },
        headerSection: {
            padding: '16px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10,
        },
        title: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '12px',
            margin: '0 0 12px 0',
        },
        searchWrapper: {
            position: 'relative',
            marginBottom: '12px',
        },
        searchIcon: {
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af',
        },
        searchInput: {
            width: '100%',
            padding: '10px 10px 10px 35px', // Padding left space for icon
            borderRadius: '9999px',
            border: '1px solid #d1d5db',
            outline: 'none',
            fontSize: '1rem',
            boxSizing: 'border-box', // Ensure padding doesn't affect width
            backgroundColor: '#f3f4f6',
        },
        newGroupBtn: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            borderRadius: '12px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            marginBottom: '16px',
            transition: 'background-color 0.2s',
        },
        newGroupIconContainer: {
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '50%',
            color: '#2563eb',
            marginRight: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        divider: {
            border: 'none',
            borderTop: '1px solid #d1d5db',
            margin: '12px 0',
        },
        toggleContainer: {
            display: 'flex',
            backgroundColor: '#e5e7eb',
            borderRadius: '8px',
            padding: '4px',
        },
        toggleButton: (isActive) => ({
            flex: 1,
            textAlign: 'center',
            padding: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s',
            backgroundColor: isActive ? 'white' : 'transparent',
            color: isActive ? '#1d4ed8' : '#4b5563',
            boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
        }),
        listContainer: {
            flex: 1,
            overflowY: 'auto',
        },
        emptyState: {
            padding: '16px',
            textAlign: 'center',
            color: '#6b7280',
        },
        // Chat Item Styles
        chatItem: (isSelected, isHovered) => ({
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            cursor: 'pointer',
            borderBottom: '1px solid #f3f4f6',
            backgroundColor: isSelected ? '#dbeafe' : (isHovered ? '#f9fafb' : 'white'), // Blue if selected, Gray if hover
            transition: 'background-color 0.2s',
        }),
        profileImg: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            objectFit: 'cover',
            marginRight: '16px',
            backgroundColor: '#e5e7eb', // Placeholder bg
        },
        textInfo: {
            flex: 1,
            minWidth: 0, // Needed for truncate
        },
        nameText: (hasUnread) => ({
            fontSize: '1rem',
            fontWeight: '600',
            color: hasUnread ? '#111827' : '#374151',
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        msgText: (hasUnread) => ({
            fontSize: '0.875rem',
            color: hasUnread ? '#1f2937' : '#6b7280',
            fontWeight: hasUnread ? '700' : '400',
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        metaInfo: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            marginLeft: '8px',
            minWidth: '50px',
        },
        timeText: (hasUnread) => ({
            fontSize: '0.75rem',
            marginBottom: '4px',
            color: hasUnread ? '#16a34a' : '#6b7280', // Green if unread
            fontWeight: hasUnread ? 'bold' : 'normal',
        }),
        badge: {
            backgroundColor: '#22c55e', // Green
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        removeBtn: {
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            padding: '6px',
            backgroundColor: 'white',
            color: '#ef4444', // Red
            borderRadius: '50%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            border: 'none',
            cursor: 'pointer',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        newChatLabel: {
            fontSize: '0.75rem',
            color: '#2563eb',
            fontWeight: '500',
        }
    };

    // --- 3. ChatListItem Component ---
    const ChatListItem = ({ chat, isSelected }) => {
        const details = getChatPartnerDetails(chat);
        const unreadCount = calculateUnreadCount(chat);
        const lastMessage = chat.lastMessage || 'Start a conversation';
        const lastMessageTime = formatTimestamp(chat.lastMessageTimestamp);
        const [isHovered, setIsHovered] = useState(false);

        const isSentByMe = chat.lastMessageSenderId === currentUserId;
        let displayMessage = lastMessage;

        if (chat.isGroup && !isSentByMe) {
            // Already includes name
        } else if (isSentByMe) {
            displayMessage = `You: ${lastMessage.replace(/^.*?: /, '')}`;
        }

        const handleChatClick = () => {
            setSelectedChat(chat);
        };

        const onRemoveClick = (e) => {
            e.stopPropagation();
            if (handleRemoveChat) {
                if (window.confirm(`Are you sure you want to remove this chat with ${details.name}?`)) {
                    handleRemoveChat(chat.id);
                }
            }
        };

        const isRemovable = !chat.isGroup;

        return (
            <div
                style={styles.chatItem(isSelected, isHovered)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleChatClick}
            >
                <img
                    src={details.profileImageUrl}
                    alt="Profile"
                    style={styles.profileImg}
                />

                <div style={styles.textInfo}>
                    <h4 style={styles.nameText(unreadCount > 0)}>
                        {details.name}
                    </h4>
                    <p style={styles.msgText(unreadCount > 0)}>
                        {displayMessage}
                    </p>
                </div>

                <div style={styles.metaInfo}>
                    <span style={styles.timeText(unreadCount > 0)}>
                        {lastMessageTime}
                    </span>
                    {unreadCount > 0 && (
                        <span style={styles.badge}>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </div>

                {/* Remove Button (Shows on Hover) */}
                {!isSelected && isHovered && isRemovable && (
                    <button
                        onClick={onRemoveClick}
                        style={styles.removeBtn}
                        title={`Remove chat with ${details.name}`}
                    >
                        <MdClose size={18} />
                    </button>
                )}
            </div>
        );
    };

    // --- 4. Main Render ---
    return (
        <div style={styles.paneContainer}>
            
            {/* Header Section */}
            <div style={styles.headerSection}>
                <h2 style={styles.title}>Messages</h2>

                {/* Search Box */}
                <div style={styles.searchWrapper}>
                    <MdSearch style={styles.searchIcon} size={20} />
                    <input
                        type="text"
                        placeholder="Search for a user..."
                        style={styles.searchInput}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* Start New Group Button */}
                {chatType === 'groups' && searchQuery.length === 0 && (
                    <div style={{ marginBottom: '16px' }}>
                        <button
                            onClick={onStartGroupChat}
                            style={styles.newGroupBtn}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                        >
                            <div style={styles.newGroupIconContainer}>
                                <MdGroupAdd size={20} />
                            </div>
                            <span style={{ fontWeight: 'bold', fontSize: '1rem' }}>Start New Group Chat</span>
                        </button>
                    </div>
                )}

                <hr style={styles.divider} />

                {/* Toggle Buttons */}
                <div style={styles.toggleContainer}>
                    <button
                        onClick={() => setChatType('chats')}
                        style={styles.toggleButton(chatType === 'chats')}
                    >
                        Chats
                    </button>
                    <button
                        onClick={() => setChatType('groups')}
                        style={styles.toggleButton(chatType === 'groups')}
                    >
                        Groups
                    </button>
                </div>
            </div>

            {/* List Section */}
            <div style={styles.listContainer}>
                {searchQuery.length > 0 ? (
                    // --- Search Results ---
                    filteredUsers.length > 0 ? (
                        filteredUsers.map(user => (
                            <div
                                key={user.id}
                                style={styles.chatItem(false, false)} // Basic style
                                // Inline hover logic for search items
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                onClick={() => handleStartNewChat(user)}
                            >
                                <img
                                    src={user.profileImageUrl || "https://placehold.co/40x40"}
                                    alt="Profile"
                                    style={styles.profileImg}
                                />
                                <div style={styles.textInfo}>
                                    <h4 style={styles.nameText(false)}>
                                        {user.fullName || user.userName || 'New User'}
                                    </h4>
                                    <p style={{ ...styles.msgText(false), fontSize: '0.8rem' }}>Tap to start chat</p>
                                </div>
                                <span style={styles.newChatLabel}>New Chat</span>
                            </div>
                        ))
                    ) : (
                        <div style={styles.emptyState}>No users found.</div>
                    )
                ) : (
                    // --- Chat List ---
                    filteredChats.length > 0 ? (
                        filteredChats.map(chat => (
                            <ChatListItem
                                key={chat.id}
                                chat={chat}
                                isSelected={selectedChat && selectedChat.id === chat.id}
                            />
                        ))
                    ) : (
                        <div style={styles.emptyState}>
                            No active {chatType === 'groups' ? 'groups' : 'chats'}.
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default ChatListPane;